import type { FormConfig, FormField } from '@connexio/shared';

/** Keep in sync with shared/src/landingPageTemplates.ts starter-field copy. */

type TemplateLocale = 'en' | 'sv';

interface FieldCopy {
  label: string;
  placeholder?: string;
}

interface StarterFieldDefinition {
  type: FormField['type'];
  copy: Record<TemplateLocale, FieldCopy>;
}

const STARTER_FIELDS: StarterFieldDefinition[] = [
  {
    type: 'text',
    copy: {
      en: { label: 'Name', placeholder: 'Your name' },
      sv: { label: 'Namn', placeholder: 'Ditt namn' },
    },
  },
  {
    type: 'email',
    copy: {
      en: { label: 'Email', placeholder: 'you@example.com' },
      sv: { label: 'E-post', placeholder: 'du@exempel.se' },
    },
  },
  {
    type: 'phone',
    copy: {
      en: { label: 'Phone', placeholder: 'Optional phone number' },
      sv: { label: 'Telefon', placeholder: 'Valfritt telefonnummer' },
    },
  },
  {
    type: 'textarea',
    copy: {
      en: { label: 'Message', placeholder: 'Tell me what you need help with' },
      sv: { label: 'Meddelande', placeholder: 'Berätta vad du behöver hjälp med' },
    },
  },
  {
    type: 'textarea',
    copy: {
      en: { label: 'Message', placeholder: 'How can I help?' },
      sv: { label: 'Meddelande', placeholder: 'Hur kan jag hjälpa till?' },
    },
  },
];

function resolveTemplateLocale(locale: string | null | undefined): TemplateLocale {
  return locale?.toLowerCase().startsWith('sv') ? 'sv' : 'en';
}

/** Translate starter-template field copy; custom labels stay as written. */
export function localizeFormField(field: FormField, locale: string | null | undefined): FormField {
  const target = resolveTemplateLocale(locale);
  const defs = STARTER_FIELDS.filter((definition) => definition.type === field.type);
  const storedPlaceholder = field.placeholder ?? '';

  const exact = defs.find((definition) =>
    (['en', 'sv'] as const).some(
      (lang) =>
        definition.copy[lang].label === field.label
        && (definition.copy[lang].placeholder ?? '') === storedPlaceholder,
    ),
  );
  if (exact) {
    const next = exact.copy[target];
    return {
      ...field,
      label: next.label,
      ...(next.placeholder !== undefined ? { placeholder: next.placeholder } : { placeholder: field.placeholder }),
    };
  }

  const byLabel = defs.find((definition) =>
    (['en', 'sv'] as const).some((lang) => definition.copy[lang].label === field.label),
  );
  if (byLabel) {
    return { ...field, label: byLabel.copy[target].label };
  }

  return field;
}

export function localizeFormConfig(config: FormConfig, locale: string | null | undefined): FormConfig {
  return {
    ...config,
    fields: config.fields.map((field) => localizeFormField(field, locale)),
  };
}
